import React, { useCallback, useState } from 'react';
import styled from '@emotion/native';
import _ from 'lodash';

import {
  Box,
  TabContainer,
  MainSurface,
  ScreenLayout,
  ScreenLoader,
  Spacer,
} from '@appComponents/ScreenLayout';
import Tabs from '@appComponents/Tabs';
import Text from '@appComponents/Text';
import { getUserTotals } from '@appUtils/accounting';
import { UserRole, TripState } from '@appUtils/tripConverter';
import { useAllNonArchivedTrips } from '@appUtils/manager';
import { formatNumber } from '@appUtils/numbers';
import { useCompanyUsers, useCompanyData } from '@appUtils/api';
import AccountingOwners from './AccountingOwners';
import AccountingPilots from './AccountingPilots';
import AccountingSetUp from './AccountingSetUp';
import AccountingTrips from './AccountingTrips';
import { ScrollView } from 'react-native';

const Accounting = ({ route }) => {
  const [activeTab, setActiveTab] = useState(
    route.params?.tab ?? AccountingTab.TRIPS,
  );
  const [company, companyLoading] = useCompanyData();
  const { data: trips } = useAllNonArchivedTrips({});
  const [owners] = useCompanyUsers(UserRole.OWNER);
  const filteredTrips = _.filter(
    trips,
    trip =>
      !_.includes([TripState.OWNER_DRAFT, TripState.PILOT_DRAFT], trip?.state),
  );
  const { totalDue: totalDueFromOwners, ...ownerTotals } = getUserTotals(
    filteredTrips,
    owners,
    UserRole.OWNER,
  );
  const [pilots] = useCompanyUsers([
    UserRole.PILOT,
    UserRole.MANAGER,
    UserRole.EMPLOYEE,
  ]);

  const { totalDue: totalDueToPilots, ...pilotTotals } = getUserTotals(
    filteredTrips,
    pilots,
    UserRole.PILOT,
  ) || { totalDue: 0 };

  const loading = companyLoading;

  if (loading) {
    return <ScreenLoader />;
  }

  return (
    <ScreenLayout alignItems="stretch">
      <AccountingTotals
        totalDueFromOwners={totalDueFromOwners}
        totalDueToPilots={totalDueToPilots}
      />
      <AccountingHeader>
        <AccountingTabs activeTab={activeTab} onChange={setActiveTab} />
      </AccountingHeader>
      <ScrollView>
        <MainSurface>
          {loading && <ScreenLoader />}
          {!loading && activeTab === AccountingTab.TRIPS && company && (
            <AccountingTrips
              company={company}
              trips={filteredTrips}
              ownerTotals={ownerTotals}
              pilotTotals={pilotTotals}
            />
          )}
          {!loading && activeTab === AccountingTab.CLIENTS && company && (
            <AccountingOwners userTotals={ownerTotals} company={company} />
          )}
          {!loading && activeTab === AccountingTab.PILOTS && company && (
            <AccountingPilots userTotals={pilotTotals} company={company} />
          )}
          {!loading && activeTab === AccountingTab.SETUP && <AccountingSetUp />}
        </MainSurface>
      </ScrollView>
    </ScreenLayout>
  );
};

const AccountingTotals = ({ totalDueFromOwners, totalDueToPilots }) => (
  <Box dir="row" jc="space-between" pl={1} pr={1.5} mb={1}>
    <TotalBox>
      <Text size="medium">Current Due from Clients</Text>
      <Spacer size={0.5} />
      <Text color="#5AFFC7" size="large">
        ${formatNumber({ value: totalDueFromOwners.toFixed(2) })}
      </Text>
    </TotalBox>
    <TotalBox>
      <Text size="medium">Current Due to Pilots</Text>
      <Spacer size={0.5} />
      <Text color="#5AFFC7" size="large">
        ${formatNumber({ value: totalDueToPilots.toFixed(2) })}
      </Text>
    </TotalBox>
    <TotalBox>
      <Text size="medium">Current Net (Clients-Pilots=Net)</Text>
      <Spacer size={0.5} />
      <Text color="#5AFFC7" size="large">
        $
        {formatNumber({
          value: (
            totalDueFromOwners.toFixed(2) - totalDueToPilots.toFixed(2)
          ).toFixed(2),
          decimalScale: 2,
        })}
      </Text>
    </TotalBox>
  </Box>
);

const TotalBox = styled(Box)(({}) => ({
  borderRadius: 8,
  backgroundColor: 'rgba(255, 255, 255, 0.06)',
  width: '32%',
  padding: 15,
}));

export const AccountingTab = {
  TRIPS: 'TRIPS',
  CLIENTS: 'CLIENTS',
  PILOTS: 'PILOTS',
  SETUP: 'EXPENSE SET UP',
};

const getAccounting = () => [
  { key: 'trips', title: AccountingTab.TRIPS },
  { key: 'clients', title: AccountingTab.CLIENTS },
  { key: 'pilots', title: AccountingTab.PILOTS },
  { key: 'setup', title: AccountingTab.SETUP },
];

const AccountingTabs = ({ activeTab, onChange }) => {
  const [routes] = useState(getAccounting);
  const handleChange = useCallback(
    ({ route }) => onChange(route.title),
    [onChange],
  );

  return (
    <Tabs
      tabIndex={routes.findIndex(tab => tab.title === activeTab)}
      onTabPress={handleChange}
      routes={routes}
      tabBarInline
    />
  );
};

const AccountingHeader = styled(TabContainer)`
  padding-bottom: 0;
`;

export default Accounting;
